import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { errorService } from '@/services/errorService'
import { getRetryDelay } from '@/utils/errorUtils'

// Get the base path from window.__BASE_PATH__ if available
// Export this function so it can be used in other files
export const getBasePath = (): string => {
  // Check if we're in a browser environment and if __BASE_PATH__ is defined
  if (typeof window !== 'undefined' && window.__BASE_PATH__) {
    return window.__BASE_PATH__
  }
  return ''
}

// Use environment variable with fallback to relative path
const basePath = getBasePath()
const API_URL = import.meta.env.VITE_API_URL || basePath

// API client initialized - removed URL logging for security

// Default configuration
const DEFAULT_TIMEOUT = 25000 // 25 seconds
const MAX_RETRIES = 2

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})



// Extend AxiosRequestConfig to include meta property
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    meta?: {
      requestTimestamp?: number;
      [key: string]: any;
    };
  }
}

// Request interceptor for API calls
axiosInstance.interceptors.request.use(
  (config) => {
    // Add request timestamp for internal tracking and timeout handling
    config.meta = {
      ...config.meta,
      requestTimestamp: Date.now()
    }

    return config
  },
  (error) => {
    // Handle request setup errors (rare)
    errorService.handleError(error, { phase: 'request' })
    return Promise.reject(error)
  }
)

// Response interceptor for API calls
axiosInstance.interceptors.response.use(
  (response) => {
    // Request processing completed successfully
    return response
  },
  async (error: AxiosError) => {
    // Get the original request config
    const originalConfig = error.config as AxiosRequestConfig & { _retry?: boolean, _retryCount?: number }

    // Skip retry for specific endpoints or methods if needed
    const skipRetryForUrls = ['/auth/logout']
    const shouldSkipRetry = originalConfig.url && skipRetryForUrls.some(url => originalConfig.url?.includes(url))

    // Initialize retry count if not present
    if (originalConfig._retryCount === undefined) {
      originalConfig._retryCount = 0
    }

    // Check if we should retry the request
    const shouldRetry = !shouldSkipRetry &&
                        originalConfig._retryCount < MAX_RETRIES &&
                        (
                          // Network errors
                          !error.response ||
                          // Server errors (5xx)
                          (error.response && error.response.status >= 500) ||
                          // Rate limiting (429)
                          (error.response && error.response.status === 429)
                        )

    if (shouldRetry) {
      originalConfig._retryCount++

      // Calculate delay with exponential backoff to avoid overwhelming the server
      const delay = getRetryDelay(originalConfig._retryCount)

      try {
        // Wait for the calculated delay before retrying
        await new Promise(resolve => setTimeout(resolve, delay))

        // Retry the request with exponential backoff strategy
        return await axiosInstance(originalConfig)
      } catch (retryError) {
        // If retry also fails, continue with error handling
        return Promise.reject(retryError)
      }
    }

    // Process the error through our error service
    errorService.handleError(error, {
      url: originalConfig.url,
      method: originalConfig.method,
      retryCount: originalConfig._retryCount
    })

    // Continue with error rejection
    return Promise.reject(error)
  }
)

export default axiosInstance